import React, { ReactNode } from 'react';
import { Upload, UploadProps } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { UploadFile, UploadChangeParam } from 'antd/es/upload/interface';
import styles from './FileUploadV2.module.scss';
import { colorPalette } from 'shared/constants/theme';

const { Dragger } = Upload;

export interface FileUploadV2Props extends UploadProps {
  /** Custom icon to display in the upload area */
  icon?: ReactNode;
  /** Title text displayed in the upload area */
  title?: string;
  /** Class name for the title element */
  titleClassName?: string;
  /** Hint/description text displayed below the title */
  hint?: string;
  /** Class name for the hint element */
  hintClassName?: string;
  /** Additional class name for the wrapper div */
  wrapperClassName?: string;
}

/**
 * FileUploadV2 - A customizable file upload component based on Ant Design's Upload.Dragger
 *
 * @example
 * // Basic usage
 * <FileUploadV2
 *   accept=".pdf,.doc,.docx"
 *   title="Click or drag file to upload"
 *   hint="Support for PDF and Word documents"
 *   onChange={(info) => console.log(info)}
 * />
 *
 * @example
 * // With custom request handler
 * <FileUploadV2
 *   accept="image/*"
 *   multiple
 *   maxCount={5}
 *   customRequest={({ file, onSuccess, onError }) => {
 *     // Custom upload logic
 *     uploadToServer(file).then(onSuccess).catch(onError);
 *   }}
 *   beforeUpload={(file) => {
 *     const isLt2M = file.size / 1024 / 1024 < 2;
 *     if (!isLt2M) message.error('File must be smaller than 2MB!');
 *     return isLt2M;
 *   }}
 * />
 *
 * @example
 * // Prevent auto upload (manual control)
 * <FileUploadV2
 *   accept=".csv"
 *   beforeUpload={() => false}
 *   showUploadList={false}
 *   onChange={({ file }) => handleFile(file)}
 * />
 */
export const FileUploadV2: React.FC<FileUploadV2Props> = ({
  // Custom props
  icon,
  title,
  titleClassName,
  hint,
  hintClassName,
  wrapperClassName,
  children,
  // Ant Design Upload props with defaults
  rootClassName,
  multiple = false,
  name = 'file',
  showUploadList = true,
  // Rest of Ant Design Upload props passed directly to Dragger
  ...rest
}) => {
  const draggerProps: UploadProps = {
    rootClassName: `${styles.dragger} ${rootClassName ?? ''}`.trim(),
    multiple,
    name,
    showUploadList,
    ...rest,
  };

  const defaultTitle = (
    <>
      <span className={styles.primaryText}>Click to upload</span>
      <span className={styles.secondaryText}> or drag and drop</span>
    </>
  );

  const renderContent = () => {
    if (children) {
      return children;
    }

    return (
      <>
        <p className="ant-upload-drag-icon">
          {icon ?? <UploadOutlined style={{ color: colorPalette.neutral5 }} />}
        </p>
        <p className={titleClassName ?? 'ant-upload-text'}>{title ?? defaultTitle}</p>
        {hint && <p className={hintClassName ?? 'ant-upload-hint'}>{hint}</p>}
      </>
    );
  };

  return (
    <div className={wrapperClassName}>
      <Dragger {...draggerProps}>{renderContent()}</Dragger>
    </div>
  );
};

export default FileUploadV2;

// Re-export useful types from antd for convenience
export type { UploadFile, UploadChangeParam, UploadProps };
